import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, DetailList, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import type { Schemas } from "../../workspace/api.generated";
import type { Command } from "../../workspace/model";
import { failure } from "../../workspace/behavior";
import { businessCodeLabel, date, money } from "../../workspace/model";
import {
  CommandDialog,
  Kpi,
  PageHeader,
  Panel,
  RowAction,
  RowLink,
  StateNotice,
  statusBadge,
} from "../../workspace/shared";
import { refundSearchFormSchema } from "../schema";
import type { RefundSearchFormValues } from "../schema";

export interface RefundViewProps {
  readonly mode?: "combined" | "list" | "detail";
  readonly filters: RefundSearchFormValues;
  readonly result?: Schemas["AdminPageRefundView"];
  readonly selected?: Schemas["RefundView"];
  readonly paymentBreakdowns?: readonly Schemas["RefundPaymentBreakdown"][];
  readonly state?: "ready" | "loading" | "error" | "denied" | "not-found";
  readonly memberId?: number;
  readonly detail?: Schemas["RefundAdminDetailView"];
  readonly refreshing?: boolean;
  readonly canCancelPayment: boolean;
  readonly canExecuteTransfer: boolean;
  readonly unresolvedRefundId?: number;
  readonly notice?: string;
  readonly recoveryError?: string;
  readonly onSearch: (filters: RefundSearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (refund: Schemas["RefundView"]) => void;
  readonly onRetry: () => void;
  readonly onRefresh: () => Promise<void>;
  readonly onReconcile: (refundId: number) => Promise<Schemas["RefundView"] | undefined>;
  readonly onCommand: (command: Command) => Promise<unknown>;
  readonly onExecuted: (refund: Schemas["RefundView"]) => Promise<void>;
  readonly detailHref?: (refund: Schemas["RefundView"]) => string;
  readonly onOpenDetail?: (refund: Schemas["RefundView"]) => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly onBack?: () => void;
  readonly memberHref?: string;
  readonly onOpenMember?: () => void;
}

const columns: readonly DataColumn<Schemas["RefundView"]>[] = [
  { key: "number", title: "환불번호", render: (row) => row.refundNo },
  { key: "reason", title: "환불 사유", render: (row) => row.refundReason === "PLAN_DOWNGRADE" ? "요금제 하향" : "회원 해지" },
  { key: "method", title: "수단", render: (row) => businessCodeLabel(row.method) },
  { key: "amount", title: "고객 환불액", numeric: true, render: (row) => money(row.customerTotalRefundAmount) },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "requested", title: "요청일시", render: (row) => date(row.requestedAt) },
];

function executionPermission(
  refund: Schemas["RefundView"],
  canCancelPayment: boolean,
  canExecuteTransfer: boolean,
): { readonly allowed: boolean; readonly reason?: string } {
  if (refund.automaticCancellation) {
    return { allowed: false, reason: refund.status === "FAILED" ? "자동 PG 취소에 실패했습니다. 결제별 결과를 확인한 뒤 처리 방식을 결정해 주세요." : refund.status === "UNKNOWN" ? "PG 취소 결과 확인이 필요합니다. 결과 확정 전에는 재취소하거나 계좌로 환불하지 마세요." : "회원 해지 접수로 자동 취소가 요청된 건입니다. 결제별 결과를 확인해 주세요." };
  }
  if (refund.status !== "REQUESTED") {
    return { allowed: false, reason: "환불 요청 상태에서만 실행할 수 있습니다." };
  }
  if (refund.method === "PG_CANCEL") {
    return canCancelPayment
      ? { allowed: true }
      : { allowed: false, reason: "PG 결제 취소 권한이 없습니다." };
  }
  if (refund.method === "BANK_TRANSFER") {
    return canExecuteTransfer
      ? { allowed: true }
      : { allowed: false, reason: "계좌 환불 실행 권한이 없습니다." };
  }
  return { allowed: false, reason: "지원하지 않는 환불 수단입니다. 최신 상태를 확인해 주세요." };
}

export function RefundView({
  mode = "combined",
  filters,
  result,
  selected,
  paymentBreakdowns,
  state = "ready",
  detail,
  refreshing = false,
  canCancelPayment,
  canExecuteTransfer,
  unresolvedRefundId,
  notice,
  recoveryError,
  onSearch,
  onPageChange,
  onSelect,
  onRetry,
  onRefresh,
  onReconcile,
  onCommand,
  onExecuted,
  detailHref,
  onOpenDetail,
  backHref,
  backLabel,
  onBack,
}: RefundViewProps) {
  const [executeOpen, setExecuteOpen] = useState(false);
  const [uncertain, setUncertain] = useState(selected?.refundId === unresolvedRefundId);
  const [refreshingSelected, setRefreshingSelected] = useState(false);
  const form = useForm<RefundSearchFormValues>({
    resolver: zodResolver(refundSearchFormSchema),
    values: filters,
    shouldFocusError: true,
  });

  useEffect(() => {
    setExecuteOpen(false);
  }, [selected?.refundId]);

  useEffect(() => {
    setUncertain(selected?.refundId === unresolvedRefundId);
  }, [selected?.refundId, unresolvedRefundId]);

  const permission = selected
    ? executionPermission(selected, canCancelPayment, canExecuteTransfer)
    : { allowed: false, reason: undefined };
  const executable = Boolean(selected && permission.allowed && !uncertain);

  const reconcile = async (refundId: number) => {
    setRefreshingSelected(true);
    try {
      const latest = await onReconcile(refundId);
      if (!latest) throw new Error("Refund state was not found during reconciliation.");
      setUncertain(false);
      return latest;
    } finally {
      setRefreshingSelected(false);
    }
  };

  const refreshPage = async () => {
    if (refreshingSelected) return;
    if (unresolvedRefundId) {
      await reconcile(unresolvedRefundId);
      return;
    }
    setRefreshingSelected(true);
    try {
      await onRefresh();
    } finally {
      setRefreshingSelected(false);
    }
  };

  const runCommand = async (command: Command) => {
    setUncertain(true);
    try {
      return await onCommand(command);
    } catch (cause) {
      if (!failure(cause).reconcile) setUncertain(false);
      throw cause;
    }
  };

  const listColumns = [
    {
      key: "number",
      title: "환불번호",
      sticky: "start" as const,
      render: (row: Schemas["RefundView"]) => detailHref ? (
        <RowLink
          triggerKey={`refund:${row.refundId}:detail`}
          href={detailHref(row)}
          onNavigate={onOpenDetail ? () => onOpenDetail(row) : undefined}
        >
          {row.refundNo}
        </RowLink>
      ) : row.refundNo,
    },
    ...columns.slice(1),
    ...(mode === "combined" ? [{
      key: "action",
      title: "작업",
      render: (row: Schemas["RefundView"]) => (
        <RowAction onClick={() => onSelect(row)}>환불 선택</RowAction>
      ),
    }] : []),
  ] satisfies readonly DataColumn<Schemas["RefundView"]>[];

  return (
    <>
      <PageHeader
        id={mode === "detail" ? "ADM-S23" : "ADM-S11"}
        title={mode === "detail" ? selected?.refundNo ?? "환불 상세" : "환불 관리"}
        description={mode === "detail" ? "서버가 계산한 환불 근거와 수단별 권한을 확인하고 현재 상태를 처리합니다." : "서버가 계산한 환불 근거와 수단별 권한을 확인한 뒤 실행합니다."}
        breadcrumbs={mode === "detail" ? [{ label: "환불 관리", href: "/refunds" }, { label: "환불 상세", href: selected ? detailHref?.(selected) ?? "#" : "#" }] : undefined}
        backAction={mode === "detail" && backHref && onBack ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
        meta={mode !== "detail" && result && state === "ready" ? (
          <p aria-live="polite">총 {result.totalCount.toLocaleString("ko-KR")}건{refreshing ? " · 최신 목록 확인 중" : ""}</p>
        ) : undefined}
        action={<Button isLoading={refreshingSelected} size="small" variant="quiet" onClick={() => { void refreshPage().catch(() => undefined); }}>최신 상태 조회</Button>}
      />
      {notice ? <Alert persistent variant="information" title="환불 처리 결과">{notice}</Alert> : null}
      {unresolvedRefundId ? <StateNotice state="conflict" onRefresh={() => { void reconcile(unresolvedRefundId).catch(() => undefined); }} /> : null}
      {recoveryError ? <Alert persistent variant="warning" title="환불 결과 미확정">{recoveryError}</Alert> : null}
      {mode !== "detail" && result && state === "ready" ? <div className="admin-kpi-grid"><Kpi label="전체 요청" value={`${result.totalCount.toLocaleString("ko-KR")}건`} /><Kpi label="현재 페이지 환불액" value={money(result.items.reduce((sum, item) => sum + item.customerTotalRefundAmount, 0))} note="전체 금액 집계는 API 확장 후 표시" /><Kpi label="처리 요청" value={`${result.items.filter((item) => item.status === "REQUESTED").length}건`} /><Kpi label="완료" value={`${result.items.filter((item) => item.status === "COMPLETED").length}건`} /></div> : null}
      {mode !== "detail" ? <><Panel title="검색 조건" description="회원 ID, 환불 상태·수단과 요청 기간을 함께 적용할 수 있습니다.">
        <form
          className="admin-filter-bar"
          noValidate
          onSubmit={form.handleSubmit((values) => onSearch({
            ...values,
            memberId: values.memberId.trim(),
          }))}
        >
          <HookFormField compact control="select" formControl={form.control} label="환불 상태" name="status">
            <option value="">전체</option>
            <option value="REQUESTED">요청됨</option>
            <option value="PROCESSING">처리 중</option>
            <option value="COMPLETED">완료</option>
            <option value="FAILED">처리 실패</option>
          </HookFormField>
          <HookFormField compact control="select" formControl={form.control} label="환불 수단" name="method">
            <option value="">전체</option>
            <option value="PG_CANCEL">PG 결제 취소</option>
            <option value="BANK_TRANSFER">계좌 환불</option>
          </HookFormField>
          <HookFormField compact formControl={form.control} inputMode="numeric" label="회원 ID" name="memberId" placeholder="숫자 ID" />
          <HookFormField compact formControl={form.control} label="요청 시작일" name="from" type="date" />
          <HookFormField compact formControl={form.control} label="요청 종료일" name="to" type="date" />
          <Button size="small" type="submit">조회</Button>
        </form>
      </Panel>
      <div>
          <DataTable
            caption="환불 검색 결과"
            columns={listColumns}
            rows={state === "ready" ? result?.items ?? [] : []}
            rowKey={(row) => row.refundId}
            state={state === "not-found" || state === "denied" ? "error" : state}
            onRetry={onRetry}
          />
          {result && state === "ready" ? (
            <Pagination
              page={result.page}
              size={result.size}
              totalCount={result.totalCount}
              onPageChange={onPageChange}
            />
          ) : null}
        </div></> : null}
      {mode === "detail" && !selected && state === "loading" ? <p role="status">환불 상세를 불러오는 중입니다.</p> : null}
      {mode === "detail" && !selected && state === "denied" ? <StateNotice state="denied" /> : null}
      {mode === "detail" && !selected && state === "not-found" ? <StateNotice state="not-found" /> : null}
      {mode === "detail" && !selected && state === "error" ? <Alert persistent variant="danger" title="환불 상세를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>현재 주소를 유지한 채 다시 조회할 수 있습니다.</Alert> : null}
        {mode !== "list" && selected ? (
          <Panel
            title="환불 계산"
            description={selected.refundNo}
          >
            {!executable ? (
              <Alert persistent variant="information" title="환불 실행 제한">
                {uncertain ? "처리 결과를 먼저 조회해 주세요." : permission.reason}
              </Alert>
            ) : null}
            <DetailList items={[
              { label: "회원명", value: detail?.memberName ?? "—" },
              { label: "휴대폰번호", value: detail?.phoneNumber ?? "—" },
              { label: "가입채널", value: businessCodeLabel(detail?.channelCode ?? "") },
              { label: "환불 사유", value: selected.refundReason === "PLAN_DOWNGRADE" ? "요금제 하향" : "회원 해지" },
              { label: "상태", value: statusBadge(selected.method === "BANK_TRANSFER" && selected.status === "UNKNOWN" ? "PROCESSING" : selected.status) },
              { label: "결제금액", value: money(selected.grossAmount) },
              { label: "결제일", value: date(detail?.paidAt) },
              { label: selected.refundReason === "PLAN_DOWNGRADE" ? "하향 신청일" : "해지일", value: date(selected.refundReason === "PLAN_DOWNGRADE" ? selected.requestedAt : detail?.terminatedAt) },
              { label: "이용일수", value: `${selected.serviceUsageDays.toLocaleString("ko-KR")}일` },
              { label: "환불계좌(은행/계좌번호)", value: [detail?.bankName, detail?.accountNumber].filter(Boolean).join(" / ") || "—" },
              { label: "환불금액", value: money(selected.customerTotalRefundAmount) },
              { label: "계산근거", value: selected.calculationText },
              { label: "예금주조회 일치 여부", value: detail?.accountVerificationStatus === "ADMIN_CONFIRMED" ? "예금주 조회 후 관리자 확인" : detail?.accountVerificationStatus === "VERIFIED" ? "일치" : detail?.accountVerificationStatus ? "미일치" : "미확인" },
            ]} />
            {selected.paymentCancellations?.length ? <Panel title="원결제별 취소 결과">
              <DataTable caption="원결제별 PG 취소 결과" rows={selected.paymentCancellations} rowKey={row => row.paymentId} columns={[
                { key: "payment", title: "원결제 ID", render: row => row.paymentId },
                { key: "kind", title: "결제 구분", render: row => row.kind === "SUBSCRIPTION" ? "기본 월정액" : "추가 결제" },
                { key: "amount", title: "취소 요청액", numeric: true, render: row => money(row.amount) },
                { key: "status", title: "취소 상태", render: row => statusBadge(row.status) },
                { key: "failure", title: "실패 코드", render: row => row.failureCode ?? "—" },
                { key: "completed", title: "취소 완료일시", render: row => date(row.completedAt) },
              ] satisfies readonly DataColumn<Schemas["RefundPaymentCancellationView"]>[]}/>
            </Panel> : null}
            {paymentBreakdowns && paymentBreakdowns.length > 0 ? (
              <Panel title="결제별 환불 계산" description={paymentBreakdowns.some(item => item.calculationPolicyVersion === 4) ? "변경 후 월정액의 하루 이용료를 한 번 올린 뒤 원결제별로 배분한 금액입니다." : "승인된 기본 월정액과 추가 결제의 이용료·기존 반환액을 각각 차감한 금액입니다."}>
                <DataTable
                  caption="원결제별 해지 환불 계산 내역"
                  columns={[
                    { key: "payment", title: "원결제 ID", render: (row) => row.paymentId },
                    { key: "kind", title: "구분", render: (row) => row.kind === "SUBSCRIPTION" ? "기본 월정액" : "추가 결제" },
                    { key: "effective", title: "이용 시작일", render: (row) => row.effectiveOn },
                    { key: "approved", title: "승인 금액", numeric: true, render: (row) => money(row.grossAmount) },
                    { key: "daily", title: "하루 이용료", numeric: true, render: (row) => row.usageChanges?.length ? "구간별 계산" : money(row.dailyUsageAmount) },
                    { key: "days", title: "이용일수", numeric: true, render: (row) => `${row.serviceUsageDays}일` },
                    { key: "usage", title: "이용료 차감", numeric: true, render: (row) => money(row.serviceUsageDeduction) },
                    { key: "returned", title: "기존 반환액", numeric: true, render: (row) => money(row.priorReturnedAmount) },
                    { key: "refund", title: "이번 환불액", numeric: true, render: (row) => money(row.refundAmount) },
                  ] satisfies readonly DataColumn<Schemas["RefundPaymentBreakdown"]>[]}
                  rows={paymentBreakdowns}
                  rowKey={(row) => row.paymentId}
                />
              </Panel>
            ) : null}
            <div className="admin-detail-actions">
              <Button size="small" disabled={!executable} onClick={() => setExecuteOpen(true)}>환불 실행</Button>
            </div>
          </Panel>
        ) : mode === "combined" ? (
          <Alert persistent variant="information" title="선택한 환불이 없습니다">
            {result?.items.length === 0
              ? "조회된 환불이 없습니다. 검색 조건을 변경해 주세요."
              : "목록에서 처리할 환불을 선택해 주세요."}
          </Alert>
        ) : null}
      {mode !== "list" && selected ? (
        <CommandDialog
          key={selected.refundId}
          open={executeOpen}
          onClose={() => setExecuteOpen(false)}
          title="환불을 실행할까요?"
          description="서버의 환불 계산과 처리 수단을 확인했습니다. 결과 확인 전에는 다시 실행하지 마세요."
          operation={selected.method === "PG_CANCEL" ? "PG 결제 취소" : "계좌 환불"}
          target={`${selected.refundNo} · 환불 ID ${selected.refundId}`}
          amount={money(selected.customerTotalRefundAmount)}
          confirmLabel="환불 실행 요청"
          reasonRequired={false}
          danger
          confirmDisabled={!executable}
          onCommand={runCommand}
          onRefresh={async () => { await reconcile(selected.refundId); }}
          onComplete={async (result) => {
            setUncertain(false);
            await onExecuted(result as Schemas["RefundView"]);
          }}
          buildCommand={() => ({
            operation: "POST /api/v1/admin/refunds/{refundId}/execute",
            params: { refundId: selected.refundId },
            body: undefined,
          })}
        />
      ) : null}
    </>
  );
}
